import { useEffect } from 'react'
import placeholderSvg from '@assets/placeholder.svg'
import { ChevronsUpDown, Cog, Cookie, ExternalLink, HelpCircle, LogOut, SquareUser } from 'lucide-react'
import { useNavigate } from 'react-router'

import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar'
import { Button } from './ui/button'

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { paths } from '@/config/path'
import { Authorization, ROLES } from '@/lib/authorization'
import { useAuthApi, useAuthenticatedUser } from '@/state/session'
import { isMac } from '@/utils/dom'
import { enforceLen } from '@/utils/format'

export const UserAvatar = () => {
  const navigate = useNavigate()
  const { logout } = useAuthApi()
  const { profile } = useAuthenticatedUser()
  const name = enforceLen({ str: profile.display_name, len: 20, ellipsis: true })

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === '?') {
        e.preventDefault()
        window.open(paths.extra.help.getHref(), '_blank')
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  const handleLogout = async () => {
    try {
      await logout()
      navigate(paths.auth.login.getHref())
    } catch (error) {
      console.error('Logout failed:', error)
      alert('Logout failed')
    }
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild className="select-none">
        <Button variant={'ghost'} className="px-0 mx-1 rounded-none hover:bg-transparent" size={'lg'}>
          <div className="flex flex-row items-center justify-between gap-2">
            <Avatar className="h-8 w-8 rounded-lg">
              <AvatarImage src={profile.picture_url || placeholderSvg} alt={name} />
              <AvatarFallback className="rounded-lg">{name[0]}</AvatarFallback>
            </Avatar>
            <div className='hidden sm:flex'>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-medium">{name}</span>
                <span className="truncate text-xs">{profile.email}</span>
              </div>
              <ChevronsUpDown className="ml-2 size-4" />
            </div>
          </div>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
        sideOffset={4}
      >
        <DropdownMenuLabel className="p-0 font-normal">
          <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
            <Avatar className="h-8 w-8 rounded-lg">
              <AvatarImage src={profile.picture_url || placeholderSvg} alt={name} />
              <AvatarFallback className="rounded-lg">{name[0]}</AvatarFallback>
            </Avatar>
            <div className="grid flex-1 text-left text-sm leading-tight">
              <span className="truncate font-medium">{name}</span>
              <span className="truncate text-xs">{profile.email}</span>
            </div>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          <DropdownMenuItem onClick={() => navigate(paths.app.settings.getHref())}>
            <Cog className="size-3.5 mr-2" />
            Mon compte
          </DropdownMenuItem>
        </DropdownMenuGroup>
        <Authorization allowedRoles={[ROLES.TECHNICIAN]}>
          {profile.slug && (
            <DropdownMenuGroup>
              <DropdownMenuItem asChild>
                <a href={paths.technician.profile.getHref(profile.slug)} target="_blank" rel="noopener noreferrer">
                  <SquareUser className="size-3.5 mr-2" />
                  Ma page publique
                  <DropdownMenuShortcut>
                    <ExternalLink className="size-3.5 ml-1" />
                  </DropdownMenuShortcut>
                </a>
              </DropdownMenuItem>
            </DropdownMenuGroup>
          )}
        </Authorization>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          <DropdownMenuItem asChild>
            <a href={paths.extra.help.getHref()} target="_blank" rel="noopener noreferrer">
              <HelpCircle className="size-3.5 mr-2" />
              Centre d'aide
              <DropdownMenuShortcut>
                <kbd>{isMac ? '⌘' : 'ctrl'}+?</kbd>
              </DropdownMenuShortcut>
            </a>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <a href={paths.extra['privacy-policy'].getHref()} target="_blank" rel="noopener noreferrer">
              <Cookie className="size-3.5 mr-2" />
              Politique de confidentialité
            </a>
          </DropdownMenuItem>
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleLogout}>
          <LogOut className="size-3.5 mr-2" />
          Se déconnecter
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
